import {
  policyType as PolicyType,
  paymentMethod as PaymentMethod,
  windHailDeductibleCanVaryByCounty,
  lapsedLicenseMonthsStates,
  reopeningStates,
  communityDriveRemoveAfter15DaysStates,
  fortifiedHomeStates
} from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { shouldOrderHomeInspection } from '@ourbranch/home-inspection-utils';
import { onboardingTasks } from '@ourbranch/onboarding-tasks';
import { getPolicyType } from 'core/helpers/policy-type';

import { appConfig } from 'brand-config';

export const hasPaymentMethod = ({
  paymentMethods,
  paymentMethod,
  policyType,
  noBindHome = false,
  noBindAuto = false
}) => {
  const { homeownersPaymentMethod, autoPaymentMethod, rentersPaymentMethod, condoPaymentMethod, farmPaymentMethod } =
    paymentMethods;
  // probably a cleverer way to do this but this works
  return (
    (policyType === PolicyType.Auto && autoPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.ARBundle && autoPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.Renters && rentersPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.Home && homeownersPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.HomeBundle && homeownersPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.AutoBundle && autoPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.HABundle && !noBindHome && !noBindAuto && homeownersPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.HABundle && !noBindHome && !noBindAuto && autoPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.HABundle && noBindHome && autoPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.HABundle && noBindAuto && homeownersPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.Condo && condoPaymentMethod === paymentMethod) ||
    (policyType === PolicyType.CABundle && [autoPaymentMethod, condoPaymentMethod].includes(paymentMethod)) ||
    (isFarmBasedPolicyType(policyType) && farmPaymentMethod === paymentMethod)
  );
};

export const positionToError = (_e, formErrors) => {
  const positionConfig = [
    { condition: (fe) => fe.homeEffectiveDate, scrollTo: '[name=homeEffectiveDate]' },
    {
      condition: (fe) => fe.mortgageDetails && fe.mortgageDetails.mortgageHolderName,
      scrollTo: '[id=mortgageSection]'
    },
    { condition: (fe) => fe.attestationsHomeAccepted, scrollTo: '[name=attestationsHomeAccepted]' },
    { condition: (fe) => fe.autoEffectiveDate, scrollTo: '[name=autoEffectiveDate]' },
    { condition: (fe) => fe.attestationsAutoAccepted, scrollTo: '[name=attestationsAutoAccepted]' },
    { condition: (fe) => fe.currentHomeownersCarrier, scrollTo: '[name=currentHomeownersCarrier]' },
    { condition: (fe) => fe.currentAutoCarrier, scrollTo: '[name=currentAutoCarrier]' },
    { condition: (fe) => fe.attestationsCondoAccepted, scrollTo: '[name=attestationsCondoAccepted]' },
    { condition: (fe) => fe.attestationsRentersAccepted, scrollTo: '[name=attestationsRentersAccepted]' }
  ];

  for (let i = 0; i < positionConfig.length; i += 1) {
    if (positionConfig[i].condition(formErrors) && document.querySelector(positionConfig[i].scrollTo)) {
      document.querySelector(positionConfig[i].scrollTo).scrollIntoView({ behavior: 'smooth', block: 'center' });
      throw formErrors;
    }
  }

  if (document.querySelector('[name=firstName]')) {
    document.querySelector('[name=firstName]').scrollIntoView({ behavior: 'smooth' });
  }
};

// Direct and Bill to other policies settle their first charge on the card or bank account picked as the
// "First charge payment method". Farm-based lines and (CFM, #36200) monoline auto use it.
const usesFirstInstallmentMethod = (policyType) => isFarmBasedPolicyType(policyType) || policyType === PolicyType.Auto;
export const isFirstInstallmentCC = ({ paymentMethod, firstInstallmentPaymentMethod, policyType }) =>
  usesFirstInstallmentMethod(policyType) &&
  (paymentMethod === PaymentMethod.Direct || paymentMethod === PaymentMethod.BillToOther) &&
  firstInstallmentPaymentMethod === PaymentMethod.CreditCard;
export const isFirstInstallmentACH = ({ paymentMethod, firstInstallmentPaymentMethod, policyType }) =>
  usesFirstInstallmentMethod(policyType) &&
  (paymentMethod === PaymentMethod.Direct || paymentMethod === PaymentMethod.BillToOther) &&
  firstInstallmentPaymentMethod === PaymentMethod.ACH;

// The payment method whose Direct / Bill to other first charge applies to this option.
const directBilledPaymentMethod = (paymentMethods, policyType) =>
  policyType === PolicyType.Auto ? paymentMethods.autoPaymentMethod : paymentMethods.farmPaymentMethod;

export const getPaymentMethods = ({
  paymentMethods,
  policyType,
  noBindHome = false,
  noBindAuto = false,
  hasAbodeProPlan = false
}) => {
  let hasCreditCard;
  let hasACH;
  let hasEscrow;
  if (paymentMethods) {
    hasCreditCard =
      hasPaymentMethod({
        paymentMethods,
        paymentMethod: PaymentMethod.CreditCard,
        policyType,
        noBindHome,
        noBindAuto
      }) ||
      hasAbodeProPlan ||
      isFirstInstallmentCC({
        policyType,
        paymentMethod: directBilledPaymentMethod(paymentMethods, policyType),
        firstInstallmentPaymentMethod: paymentMethods.directBillPreferredPaymentMethod
      });

    hasACH =
      hasPaymentMethod({
        paymentMethods,
        paymentMethod: PaymentMethod.ACH,
        policyType,
        noBindHome,
        noBindAuto
      }) ||
      isFirstInstallmentACH({
        policyType,
        paymentMethod: directBilledPaymentMethod(paymentMethods, policyType),
        firstInstallmentPaymentMethod: paymentMethods.directBillPreferredPaymentMethod
      });

    hasEscrow = hasPaymentMethod({
      paymentMethods,
      paymentMethod: PaymentMethod.Escrow,
      policyType,
      noBindHome,
      noBindAuto
    });
  }

  return { hasCreditCard, hasACH, hasEscrow };
};

const hasExcludedKiaOrHyundai = (cars) => {
  const matches = cars.filter((car) => {
    const { make, model, year, doors } = car;

    if (!make || !model || !year || !doors) {
      return false;
    }

    const lowerCaseMake = make.toLowerCase();
    const lowerCaseModel = model.toLowerCase();

    // Kia
    const excludedKias =
      (lowerCaseMake === 'kia' &&
        lowerCaseModel.match(/(forte|sedona|optima|rio|soul|sportage)/i) &&
        year >= 2015 &&
        year <= 2021) ||
      (lowerCaseModel.match(/(optima hybrid|sorento)/i) && year >= 2015 && year <= 2016);

    // Hyundai
    const excludedVelosters = year >= 2015 && year <= 2018 && lowerCaseModel === 'veloster';
    const excludedElantras = lowerCaseModel === 'elantra' && (doors === 2 || doors === 4);

    const excludedHyundais =
      lowerCaseMake === 'hyundai' &&
      ((year >= 2015 && year <= 2021 && (lowerCaseModel.match(/(accent|kona|santa fe|tucson)/i) || excludedElantras)) ||
        excludedVelosters);

    return !!(excludedKias || excludedHyundais);
  });
  return matches.length > 0;
};

export const getPreSaleChecklistConditions = (quote, values, selectedOption, offer, tasksWithDocuments = []) => {
  const {
    correctedAddress,
    drivers,
    cars,
    homeCoverage,
    home,
    includeEarthquake,
    includeFlood,
    global,
    scheduledPersonalProperty,
    connectedHome
  } = quote;
  const primaryDriver = drivers?.find((driver) => driver.isPrimary);

  const { state } = correctedAddress;
  const { interestedInSeparateWindHail, windHailExclusion, coverageRSExtended } = homeCoverage || {};
  const { breakupWithAuto, breakupWithHomeowners, breakupWithRenters } = values;
  const primaryIsMarried = primaryDriver?.maritalStatus === 'M';
  const stressFreeSwitch =
    shouldShowStressFreeSwitch({ selectedOption, noBindHome: quote.noBindHome, noBindAuto: quote.noBindAuto }) &&
    (breakupWithAuto || breakupWithHomeowners || breakupWithRenters);
  const noWindHailInformed =
    windHailExclusion &&
    ((state === 'AL' && !interestedInSeparateWindHail) ||
      (windHailDeductibleCanVaryByCounty[state] && windHailDeductibleCanVaryByCounty[state][home.county]));

  // https://github.com/gobranch/branch/issues/19449
  // if any of these are on the offer, the member needs to give proof within a certain amount of days
  const hasSPP =
    scheduledPersonalProperty &&
    scheduledPersonalProperty?.items?.length > 0 &&
    !tasksWithDocuments.includes(onboardingTasks.NeedScheduledPersonalPropertyImages);
  const hasInventoryDiscount = global.discountInventoryScore;
  const hasConnectedHome =
    connectedHome &&
    connectedHome?.monitored !== 'N' &&
    !tasksWithDocuments.includes(onboardingTasks.ProvideProofOfConnectedHome);
  // Person-specific tasks use unique keys (e.g. "Provide Proof of Good Student:John Doe")
  // so we check per-driver whether their specific task has a document uploaded
  const goodStudentDrivers = drivers?.filter((d) => d.goodStudent) || [];
  const hasGoodStudent =
    goodStudentDrivers.length > 0 &&
    goodStudentDrivers.some(
      (d) => !tasksWithDocuments.includes(`${onboardingTasks.ProvideProofOfGoodStudent}:${d.firstName} ${d.lastName}`)
    );
  const drivingCourseDrivers = drivers?.filter((d) => d.takenCourse) || [];
  const hasTakenDrivingCourse =
    drivingCourseDrivers.length > 0 &&
    drivingCourseDrivers.some(
      (d) =>
        !tasksWithDocuments.includes(
          `${onboardingTasks.NeedProofThatAccidentPreventionCourseTaken}:${d.firstName} ${d.lastName}`
        )
    );
  const hasKiaOrHyundai = cars ? hasExcludedKiaOrHyundai(cars) : false;
  const homeAgeInventoryDiscountInformed =
    hasInventoryDiscount &&
    selectedOption?.includes(PolicyType.Home) &&
    reopeningStates.isStateReopenedForPolicyType(state, PolicyType.Home) &&
    home?.yearBuilt <= 1940;
  const homeInspectionInformed =
    selectedOption?.includes(PolicyType.Home) &&
    offer &&
    shouldOrderHomeInspection({
      offer
    });

  const hasFortifiedHome = fortifiedHomeStates.includes(state) && home?.fortifiedHome > 0;

  return {
    buyingCondoPolicyInformed: selectedOption.includes(PolicyType.Condo),
    allApplicantsConfirmed: primaryIsMarried,
    inColoradoInformed: state === 'CO',
    priorInsuranceEntered: stressFreeSwitch,
    noWindHailInformed,
    // #35626: never for a brand without Community Drive (CFM), whatever a stale driver flag says.
    communityDriveInformed:
      !appConfig?.featureFlags?.hideCommunityDrive &&
      communityDriveRemoveAfter15DaysStates.includes(state) &&
      drivers.some((d) => d.communityDriveParticipation) &&
      selectedOption.includes(PolicyType.Auto),
    earthquakeAndFloodInformed: includeEarthquake || includeFlood,
    proofOfPriorCoverageInformed:
      selectedOption?.includes(PolicyType.Auto) &&
      ['CO', 'KY', 'ME', 'MI', 'NH', 'TX'].includes(state) &&
      (!global.receivedCVData || !global.hadInforceCVPolicy),
    lapsedLicensesVerified: lapsedLicenseMonthsStates[state] && quote?.selectedOption?.includes(PolicyType.Auto),
    excludedDriversInformed: drivers?.some((d) => d.excludeDriver),
    roofSurfacesEndorsementInformed: selectedOption?.includes(PolicyType.Home) && !coverageRSExtended,
    driversLicenseInformed:
      selectedOption?.includes(PolicyType.Auto) && drivers?.find((driver) => driver.driversLicenseNumber === null),
    michiganPIPInformed: state === 'MI' && selectedOption.includes(PolicyType.Auto),
    trailingDocsInformed:
      hasSPP ||
      (hasInventoryDiscount && !homeAgeInventoryDiscountInformed) ||
      hasConnectedHome ||
      hasGoodStudent ||
      hasTakenDrivingCourse ||
      hasKiaOrHyundai,
    hasSPP,
    hasInventoryDiscount,
    hasConnectedHome,
    hasGoodStudent,
    hasTakenDrivingCourse,
    hasKiaOrHyundai,
    homeAgeInventoryDiscountInformed,
    homeInspectionInformed,
    hasFortifiedHome
  };
};

// #36286: a brand that doesn't cancel the prior carrier's policy on the member's behalf has no
// Stress-Free Switch. Read in one place — the section's render AND the checkout's initial values —
// because the buy schema requires the prior carrier + e-signature whenever a breakupWith* is true and
// both Purchase and Send Link forward them, so a value seeded from an offer saved before the opt-out
// would block checkout on fields the agent can't see, or still ask the prior carrier to cancel.
export const isStressFreeSwitchHidden = () => Boolean(appConfig?.featureFlags?.hideStressFreeSwitch);

export const shouldShowStressFreeSwitch = ({ selectedOption, noBindHome, noBindAuto }) => {
  const { isCondo, hasFarm } = getPolicyType(selectedOption, noBindHome, noBindAuto);
  return !isCondo && !hasFarm && !isStressFreeSwitchHidden();
};

export const getStressFreeSwitchBindInformation = (values, showStressFreeSwitch) => {
  if (!showStressFreeSwitch) {
    return {
      breakupWithAuto: null,
      breakupWithHomeowners: null,
      breakupWithRenters: null,
      currentAutoCarrier: null,
      currentAutoCarrierPolicyNumber: null,
      currentHomeownersCarrier: null,
      currentHomeownersCarrierPolicyNumber: null,
      currentRentersCarrier: null,
      currentRentersCarrierPolicyNumber: null
    };
  }

  return {
    breakupWithAuto: values.breakupWithAuto,
    breakupWithHomeowners: values.breakupWithHomeowners,
    breakupWithRenters: values.breakupWithRenters,
    currentAutoCarrier: values.breakupWithAuto ? values.currentAutoCarrier : null,
    currentAutoCarrierPolicyNumber: values.breakupWithAuto ? values.currentAutoCarrierPolicyNumber : null,
    currentHomeownersCarrier: values.breakupWithHomeowners ? values.currentHomeownersCarrier : null,
    currentHomeownersCarrierPolicyNumber: values.breakupWithHomeowners
      ? values.currentHomeownersCarrierPolicyNumber
      : null,
    currentRentersCarrier: values.breakupWithRenters ? values.currentRentersCarrier : null,
    currentRentersCarrierPolicyNumber: values.breakupWithRenters ? values.currentRentersCarrierPolicyNumber : null
  };
};

export const getStressFreeSwitchBuyBindDetails = ({ values, showStressFreeSwitch, includeUmbrella }) => ({
  ...getStressFreeSwitchBindInformation(values, showStressFreeSwitch),
  breakupWithUmbrella: showStressFreeSwitch ? Boolean(includeUmbrella && values.breakupWithHomeowners) : null,
  breakupESignature:
    showStressFreeSwitch && (values.breakupWithAuto || values.breakupWithHomeowners || values.breakupWithRenters)
      ? values.breakupESignature
      : null
});

export const seedStressFreeSwitch = ({ breakupWithAuto, breakupWithHomeowners, breakupWithRenters }) => {
  if (isStressFreeSwitchHidden()) {
    return { breakupWithAuto: null, breakupWithHomeowners: null, breakupWithRenters: null };
  }
  return {
    breakupWithAuto: breakupWithAuto || null,
    breakupWithHomeowners: breakupWithHomeowners || null,
    breakupWithRenters: breakupWithRenters || null
  };
};

export const getExtraQuestionsConditions = (quote, isAgency) => {
  const { selectedOption, global } = quote;
  const { affinity } = global;
  // #36287: a brand that already records the companion line on the offer (CFM's Companion Policy
  // section, #35434) doesn't ask its agents a second time at checkout.
  if (appConfig?.featureFlags?.skipCheckoutOpposingPolicyQuestion) {
    return { opposingPolicySoldByAgency: false };
  }
  const opposingPolicySoldByAgency =
    (selectedOption === PolicyType.Home || selectedOption === PolicyType.Auto) && affinity && isAgency;

  return { opposingPolicySoldByAgency };
};
